/**
 * Dependencies:
 * - Custom select component
 *
 * Required translation strings:
 * - addToCart
 * - noStock
 * - noVariant
 * - onlyXLeft
 */

if (!customElements.get("variant-picker")) {
  class VariantPicker extends HTMLElement {
    constructor() {
      super();
      this.section = this.closest(".js-product");
      this.productForm = this.section.querySelector(".js-product-form");
      this.optionSelectors = this.querySelectorAll(
        ".option-selector:not(.option-selector--custom)"
      );
      this.nativeSelector = document.getElementById(
        `variants-${this.section.dataset.section}`
      );
      this.data = this.getVariantData();
      this.selectedOptions = this.getSelectedOptions();
      this.variant = this.getSelectedVariant();

      this.addEventListener("change", this.handleVariantChange.bind(this));
      this.applySearchParams();

      // Set a flag to track initialization
      this.isInitialized = false;
    }

    connectedCallback() {
      if (!this.isInitialized) {
        this.init();
        this.isInitialized = true;
      }
    }

    init() {
      // Get current variant on load
      const urlParams = new URLSearchParams(window.location.search);
      const variantId = urlParams.get("variant");

      if (variantId) {
        this.variant = this.data.variants.find(
          (v) => v.id.toString() === variantId
        );
      } else {
        this.variant = this.getSelectedVariant();
      }

      // Run initial updates
      this.updateAvailability();
      this.updateAddToCartButton();
      // this.updateBackorderText();
      this.updatePrice();
      this.updatePickupAvailability();
      this.updateSku();
      this.updateBarcode();

      // Set loaded attribute after initialization
      this.setAttribute("loaded", "");

      // Dispatch initialization event
      this.dispatchEvent(
        new CustomEvent("on:variant:init", {
          bubbles: true,
          detail: {
            form: this.productForm,
            variant: this.variant,
            allVariants: this.data.variants,
            selectedOptions: this.selectedOptions,
          },
        })
      );
    }

    /**
     * Handles 'change' events on the variant picker element.
     * @param {object} evt - Event object.
     */
    handleVariantChange(evt) {
      this.selectedOptions = this.getSelectedOptions();
      this.variant = this.getSelectedVariant();

      this.preSelection =
        !this.variant && this.selectedOptions.find((o) => o === null) === null;

      this.updateUrl(evt);
      this.updateVariantInput();
      this.updateAddToCartButton();
      this.updateAvailability();
      this.updatePrice();
      // this.updateBackorderText();
      this.updatePickupAvailability();
      this.updateSku();
      this.updateBarcode();
      VariantPicker.updateLabelText(evt);

      this.dispatchEvent(
        new CustomEvent("on:variant:change", {
          bubbles: true,
          detail: {
            form: this.productForm,
            variant: this.variant,
            allVariants: this.data.variants,
            selectedOptions: this.selectedOptions,
          },
        })
      );
    }

    updateAddToCartButton() {
      if (!this.productForm) return;
      if (this.selectedOptions.indexOf(null) > -1) return;

      this.addBtn =
        this.addBtn || this.productForm.querySelector('[name="add"]');
      const variantAvailable = this.variant && this.variant.available;
      const unavailableStr = this.variant
        ? theme.strings.noStock
        : theme.strings.noVariant;

      // this.addBtn.disabled = !variantAvailable;
      // this.addBtn.textContent = variantAvailable
      //   ? this.addBtn.dataset.addToCartText
      //   : unavailableStr;
    }

    /**
     * Updates the availability status of an option.
     * @param {Element} optionEl - Option element.
     * @param {boolean} exists - Does this option lead to a variant that exists?
     * @param {boolean} available - Does this option lead to a variant that is available to buy?
     */
    static updateOptionAvailability(optionEl, exists, available) {
      const el = optionEl;
      const unavailableText = exists
        ? theme.strings.noStock
        : theme.strings.noVariant;
      el.classList.toggle("is-unavailable", !available);
      el.classList.toggle("is-nonexistent", !exists);

      if (optionEl.classList.contains("custom-select__option")) {
        const em = el.querySelector("em");

        if (em) {
          em.hidden = available;
        }

        if (!available) {
          if (em) {
            em.textContent = unavailableText;
          } else {
            el.innerHTML = `${el.innerHTML} <em class="pointer-events-none">${unavailableText}</em>`;
          }
        }
      } else if (!available) {
        el.nextElementSibling.title = unavailableText;
      } else {
        el.nextElementSibling.removeAttribute("title");
      }
    }

    /**
     * Updates the availability status in option selectors.
     */
    updateAvailability() {
      if (this.dataset.availability === "off") return;
      const { availabilityMode } = this.dataset; // 'down' or 'selection'
      let currVariant = this.variant;

      if (!this.variant) {
        currVariant = { options: this.selectedOptions };
      }

      if (availabilityMode === "selection") {
        // Flag all options as unavailable
        this.querySelectorAll(".js-option").forEach((optionEl) => {
          VariantPicker.updateOptionAvailability(optionEl, false, false);
        });

        // Flag selector options as available or sold out, depending on the variant availability
        this.optionSelectors.forEach((selector, selectorIndex) => {
          this.data.variants.forEach((variant) => {
            let matchCount = 0;

            variant.options.forEach((option, optionIndex) => {
              if (
                option === currVariant.options[optionIndex] &&
                optionIndex !== selectorIndex
              ) {
                matchCount += 1;
              }
            });

            if (matchCount === currVariant.options.length - 1) {
              const options = selector.querySelectorAll(".js-option");
              const optionEl = Array.from(options).find((opt) => {
                if (selector.dataset.selectorType === "dropdown") {
                  return opt.dataset.value === variant.options[selectorIndex];
                }
                return opt.value === variant.options[selectorIndex];
              });

              if (optionEl) {
                VariantPicker.updateOptionAvailability(
                  optionEl,
                  true,
                  variant.available
                );
              }
            }
          });
        });
      } else {
        this.optionSelectors.forEach((selector, selectorIndex) => {
          const options = selector.querySelectorAll(
            '.js-option:not([data-value=""])'
          );
          options.forEach((option) => {
            const optionValue =
              selector.dataset.selectorType === "dropdown"
                ? option.dataset.value
                : option.value;
            // any available variants with previous options and this one locked in?
            let variantsExist = false;
            let variantsAvailable = false;
            this.data.variants.forEach((v) => {
              let matches = 0;
              for (let i = 0; i < selectorIndex; i += 1) {
                if (
                  v.options[i] === this.selectedOptions[i] ||
                  this.selectedOptions[i] === null
                ) {
                  matches += 1;
                }
              }
              if (
                v.options[selectorIndex] === optionValue &&
                matches === selectorIndex
              ) {
                variantsExist = true;
                if (v.available) {
                  variantsAvailable = true;
                }
              }
            });
            VariantPicker.updateOptionAvailability(
              option,
              variantsExist,
              variantsAvailable
            );
          });
        });
      }
    }

    /**
     * Updates the backorder text and visibility.
     */

    // updateBackorderText() {
    //   console.log("updateBackorderText called");

    //   const backorderEl = this.section.querySelector(".backorder");
    //   const addToCartBtn = this.section.querySelector('button[name="add"]');
    //   const preorderInput = this.section.querySelector(
    //     'input[name="properties[Expected to be In-Stock]"]'
    //   );

    //   // Always reset everything to Add to Cart default
    //   if (backorderEl) {
    //     backorderEl.style.display = "none";
    //     backorderEl.hidden = true;
    //   }

    //   if (preorderInput) {
    //     preorderInput.setAttribute("disabled", "disabled");
    //     preorderInput.style.display = "none";
    //   }

    //   this.section.classList.remove("is-preorder");

    //   if (addToCartBtn) {
    //     const defaultText = "Add to Cart";
    //     addToCartBtn.textContent = defaultText;
    //     addToCartBtn.innerHTML = defaultText;
    //     addToCartBtn.setAttribute("data-add-to-cart-text", defaultText);
    //   }

    //   // If no valid variant selected, stop here
    //   if (!this.variant || !this.variant.available) {
    //     console.log("No valid or available variant");
    //     return;
    //   }

    //   // Try to locate the variant option element
    //   let variantOption =
    //     this.section.querySelector(`option[value="${this.variant.id}"]`) ||
    //     this.section.querySelector(`[data-variant-id="${this.variant.id}"]`) ||
    //     this.nativeSelector?.querySelector(
    //       `option[value="${this.variant.id}"]`
    //     );

    //   if (!variantOption) {
    //     console.log("No matching variant option element found");
    //     return;
    //   }

    //   const stockStatus = variantOption.getAttribute("data-stock");
    //   const preorderValue = preorderInput?.value?.trim();

    //   console.log("Stock status:", stockStatus);
    //   console.log("Preorder input value:", preorderValue);

    //   const shouldShowPreorder =
    //     stockStatus === "out" || (preorderValue && preorderValue !== "-");

    //   if (shouldShowPreorder) {
    //     console.log("Show PREORDER");

    //     if (backorderEl) {
    //       backorderEl.style.display = "";
    //       backorderEl.hidden = false;
    //     }

    //     if (addToCartBtn) {
    //       const preorderText = "PREORDER";
    //       addToCartBtn.textContent = preorderText;
    //       addToCartBtn.innerHTML = preorderText;
    //       addToCartBtn.setAttribute("data-add-to-cart-text", preorderText);
    //     }

    //     if (preorderInput) {
    //       preorderInput.removeAttribute("disabled");
    //       preorderInput.style.display = "";
    //     }

    //     this.section.classList.add("is-preorder");
    //   } else {
    //     console.log(
    //       "Stock is available or preorder value empty — Add to Cart remains"
    //     );
    //   }
    // }

    /**
     * Updates the colour option label text.
     * @param {object} evt - Event object
     */
    static updateLabelText(evt) {
      const selector = evt.target.closest(".option-selector");
      if (selector.dataset.selectorType === "dropdown") return;

      const colorText = selector.querySelector(".js-color-text");
      if (!colorText) return;

      colorText.textContent =
        evt.target.nextElementSibling.querySelector(".js-value").textContent;
    }

    /**
     * Updates the pick up availability.
     */
    updatePickupAvailability() {
      this.pickUpAvailability =
        this.pickUpAvailability ||
        this.section.querySelector("pickup-availability");

      if (!this.pickUpAvailability) return;

      customElements.whenDefined("pickup-availability").then(() => {
        // Re-query in case Shopify replaced the node
        this.pickUpAvailability =
          this.section.querySelector("pickup-availability");

        if (
          this.variant &&
          this.variant.available &&
          this.pickUpAvailability &&
          typeof this.pickUpAvailability.getAvailability === "function"
        ) {
          this.pickUpAvailability.getAvailability(this.variant.id);
        } else if (this.pickUpAvailability) {
          this.pickUpAvailability.removeAttribute("available");
          this.pickUpAvailability.innerHTML = "";
        }
      });
    }
    


    /**
     * Updates the price.
     */
    updatePrice() {
      this.price =
        this.price || this.section.querySelector(".product-info__price .price");
      if (!this.price) return;

      let { variant } = this;
      if (this.preSelection) {
        variant = this.data.variants[0];
        for (let i = 1; i < this.data.variants.length; i += 1) {
          if (this.data.variants[i].price < variant.price)
            variant = this.data.variants[i];
        }
      }

      if (variant) {
        const priceCurrentEl = this.price.querySelector(".price__current");
        const priceWasEl = this.price.querySelector(".price__was");
        const unitPriceEl = this.price.querySelector(".unit-price");

        const formatted = this.data.formatted[variant.id] || {};
        const formattedPrice = formatted.price;
        const formattedCompareAtPrice = formatted.compareAtPrice;

        const discountConfig = this.data.productDiscount || {};
        const discountEnabled = !!discountConfig.enabled;
        const discountPercent = Number(discountConfig.percent || 0);
        const discountType = discountConfig.type || "other";
        const isBundle = discountType === "bundle";

        const hasCustomDiscount =
          discountEnabled &&
          !isBundle && // Do not apply custom discount logic for bundle type
          discountPercent > 0 &&
          !!formatted.discountedPriceFormatted;

        // Apply theme discount using preformatted values from Liquid
        if (hasCustomDiscount) {
          if (priceCurrentEl) {
            priceCurrentEl.innerHTML = formatted.discountedPriceFormatted;
          }
          if (priceWasEl) {
            // Original base price from Liquid
            priceWasEl.innerHTML = formattedPrice || "";
          }

          // Force sale state
          this.price.classList.add("price--on-sale");
        } else {
          // No custom discount or bundle type: fallback to original behavior
          if (priceCurrentEl) {
            priceCurrentEl.innerHTML = formattedPrice;
          }
          if (priceWasEl) {
            priceWasEl.innerHTML = formattedCompareAtPrice || "";
          }

          this.price.classList.toggle(
            "price--on-sale",
            variant.compare_at_price > variant.price
          );
        }

        // Unit price
        if (variant.unit_price_measurement) {
          const valueEl = this.price.querySelector(".unit-price__price");
          const unitEl = this.price.querySelector(".unit-price__unit");
          const value = variant.unit_price_measurement.reference_value;
          const unit = variant.unit_price_measurement.reference_unit;

          if (valueEl && formatted.unitPrice) {
            valueEl.innerHTML = formatted.unitPrice;
          }
          if (unitEl) {
            unitEl.textContent = value === 1 ? unit : `${value} ${unit}`;
          }
          if (unitPriceEl) unitPriceEl.hidden = false;
        } else if (unitPriceEl) {
          unitPriceEl.hidden = true;
        }

        // Sold out state
        this.price.classList.toggle(
          "price--sold-out",
          !variant.available && !this.preSelection
        );
      }

      this.price.querySelector(".price__default").hidden =
        !this.variant && !this.preSelection;
      this.price.querySelector(".price__no-variant").hidden =
        this.variant || this.preSelection;
      const from = this.price.querySelector(".price__from");
      if (from) {
        from.hidden = !this.preSelection;
      }
    }

    /**
     * Updates the SKU.
     */
    updateSku() {
      this.sku = this.sku || this.section.querySelector(".product-sku__value");
      if (!this.sku) return;

      const skuAvailable = this.variant && this.variant.sku;
      this.sku.textContent = skuAvailable ? this.variant.sku : "";
      this.sku.parentNode.hidden = !skuAvailable;
    }

    /**
     * Updates the Barcode.
     */
    updateBarcode() {
      this.barcode =
        this.barcode || this.section.querySelector(".product-barcode__value");
      if (!this.barcode) return;

      const barcodeAvailable = this.variant && this.variant.barcode;
      this.barcode.textContent = barcodeAvailable ? this.variant.barcode : "";
      this.barcode.parentNode.hidden = !barcodeAvailable;
    }

    /**
     * Updates the url with the selected variant id.
     * @param {object} evt - Event object.
     */
    updateUrl(evt) {
      if (!evt || evt.type !== "change" || this.dataset.updateUrl === "false")
        return;
      // const url = this.variant
      //   ? `${this.dataset.url}?variant=${this.variant.id}`
      //   : this.dataset.url;
      // window.history.replaceState({}, "", url);
      const url = new URL(window.location.href); // Get the current URL
      const params = new URLSearchParams(url.search); // Get existing query parameters

      if (this.variant) {
        params.set("variant", this.variant.id); // Update only the variant ID
      } else {
        params.delete("variant"); // Remove the variant param if no variant is selected
      }

      // Construct the new URL without changing the product handle
      const newUrl = `${url.origin}${url.pathname}?${params.toString()}`;

      console.log("test area url", newUrl);

      window.history.replaceState({}, "", newUrl);
    }

    /**
     * Updates the value of the hidden [name="id"] form inputs.
     */
    updateVariantInput() {
      this.forms =
        this.forms ||
        this.section.querySelectorAll(".js-product-form, .js-instalments-form");

      this.forms.forEach((form) => {
        const input = form.querySelector('[name="id"]');
        input.value = this.variant ? this.variant.id : "";
        // Dispatch both events to ensure compatibility
        input.dispatchEvent(new Event("change", { bubbles: true }));
      });

      console.log("updateVariantInput")
    }

    /**
     * Gets the variant data for a product.
     * @returns {?object}
     */
    // getVariantData() {
    //   const dataEl = this.querySelector('[type="application/json"]');
    //   return JSON.parse(dataEl.textContent);
    // }
    getVariantData() {
      const dataEl = this.querySelector('[type="application/json"]');

      if (!dataEl || !dataEl.textContent.trim()) {
        console.error("Variant JSON data missing");
        return { variants: [], formatted: {} };
      }

      try {
        return JSON.parse(dataEl.textContent);
      } catch (error) {
        console.error("Failed to parse variant JSON", error);
        console.log("JSON content:", dataEl.textContent);

        return { variants: [], formatted: {} };
      }
    }

    /**
     * Get the selected values from a list of variant options.
     * @returns {Array} Array of selected option values, value is null if not selected.
     */
    getSelectedOptions() {
      const selectedOptions = [];

      this.optionSelectors.forEach((selector) => {
        if (selector.dataset.selectorType === "dropdown") {
          const selected = selector.querySelector(
            '.custom-select__option[aria-selected="true"]'
          );
          selectedOptions.push(
            selected && selected.dataset.value !== ""
              ? selected.dataset.value
              : null
          );
        } else {
          const selected = selector.querySelector("input:checked");
          selectedOptions.push(selected ? selected.value : null);
        }
      });

      return selectedOptions;
    }

    /**
     * Get selected variant data.
     * @returns {?object} Variant object, or null if one is not selected.
     */
    getSelectedVariant() {
      return this.data.variants.find((v) =>
        v.options.every((val, index) => val === this.selectedOptions[index])
      );
    }

    /**
     * Apply search parameters.
     */
    applySearchParams() {
      const searchParams = new URLSearchParams(window.location.search);

      Array.from(searchParams.keys()).forEach((key) => {
        const value = searchParams.get(key);
        const optionSelectors = Array.from(this.optionSelectors);
        const matchingOptionSelector = optionSelectors.find(
          (x) => x.dataset.option === key
        );
        if (!matchingOptionSelector) return;

        if (matchingOptionSelector.dataset.selectorType === "dropdown") {
          const colorOptionDropdown = matchingOptionSelector.querySelector(
            `.custom-select__option[data-value="${value}"]`
          );
          if (colorOptionDropdown) {
            const customSelect = colorOptionDropdown.closest("custom-select");
            customSelect.selectOption(colorOptionDropdown);
          }
        } else {
          const matchingInput = matchingOptionSelector.querySelector(
            `input[value="${value}"]`
          );

          if (matchingInput) {
            const matchingOptionValue = matchingOptionSelector.querySelector(
              ".option-selector__label-value"
            );
            if (matchingOptionValue) {
              matchingOptionSelector.querySelector(
                ".option-selector__label-value"
              ).textContent = value;
            }
            matchingInput.checked = true;
            matchingInput.dispatchEvent(
              new CustomEvent("change", { bubbles: true, cancelable: false })
            );
          }
        }
      });
    }

  }

  // Define the custom element
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => {
      customElements.define("variant-picker", VariantPicker);
    });
  } else {
    customElements.define("variant-picker", VariantPicker);
  }
}


(function () {

  function setImageUrl(el, url) {
    if (!el || !url) return;
    // Si es <img> directo
    if (el.tagName && el.tagName.toLowerCase() === 'img') {
      el.style.opacity = 0;
      el.onload = () => { el.style.opacity = ''; el.onload = null; };
      el.src = url;
      // también setear srcset como fallback sencillo
      try { el.srcset = url; } catch (e) { }
      return;
    }

    const img = el.querySelector && (el.querySelector('img') || el.querySelector('picture img'));
    if (img) {
      img.style.opacity = 0;
      img.onload = () => { img.style.opacity = ''; img.onload = null; };
      img.src = url;
      try { img.srcset = url; } catch (e) { }
      // actualizar sources si existen
      const sources = el.querySelectorAll ? el.querySelectorAll('source') : [];
      sources.forEach((s) => {
        try { s.srcset = url; } catch (err) { }
      });
      return;
    }

    // fallback: background-image
    el.style.backgroundImage = `url(${url})`;
  }

  function findContainerWithHoverSwap(startEl) {
    let el = startEl;
    while (el && el !== document.body) {
      if (el.querySelector && el.querySelector('.hover-swap')) return el;
      el = el.parentElement;
    }
    return null;
  }

  function getVariantImageUrl(variant) {
    if (!variant) return null;
    return (
      variant.featured_media?.preview_image?.src ||
      variant.featured_media?.src ||
      variant.featured_image?.src ||
      variant.featured_image?.url ||
      variant.image?.src ||
      variant.image?.url ||
      null
    );
  }

  // handler para init/change events
  function handleVariantEvent(evt) {

    const vpEl = evt.target && evt.target.closest ? evt.target.closest('variant-picker') || evt.target : evt.target;
    const detail = evt.detail || {};
    const variant = detail.variant || detail; // por si acaso
    if (!variant) return;

    const container = findContainerWithHoverSwap(vpEl);
    if (!container) return;

    const hoverSwap = container.querySelector('.hover-swap');
    if (!hoverSwap) return;

    const primaryEl =
      hoverSwap.querySelector('img.hover-swap__img--primary') ||
      hoverSwap.querySelector('.hover-swap__img--primary') ||
      hoverSwap.querySelector('picture .hover-swap__img--primary') ||
      hoverSwap.querySelector('.hover-swap__img--primary');

    const secondaryEl =
      hoverSwap.querySelector('img.hover-swap__img--secondary') ||
      hoverSwap.querySelector('.hover-swap__img--secondary') ||
      hoverSwap.querySelector('picture .hover-swap__img--secondary') ||
      hoverSwap.querySelector('.hover-swap__img--secondary');


    const imgUrl = getVariantImageUrl(variant);
    if (imgUrl && primaryEl) {
      setImageUrl(primaryEl, imgUrl);
    }

    try {
      const pos = variant.featured_media?.position;
      if (typeof pos === 'number' && secondaryEl) {

        const jsonScript =
          vpEl.querySelector('script[type="application/json"]') ||
          container.querySelector('script[type="application/json"]');

        if (jsonScript) {
          let parsed = null;
          try { parsed = JSON.parse(jsonScript.textContent); } catch (err) { parsed = null; }

          const media = parsed?.media || parsed?.images || parsed?.product_media || parsed?.media || null;
          if (Array.isArray(media)) {
            const next = media.find((m) => m.position === pos + 1);
            const nextUrl = next?.preview_image?.src || next?.src || next?.image?.src || null;
            if (nextUrl) setImageUrl(secondaryEl, nextUrl);
          }
        }
      }
    } catch (err) {
      // console.warn('no se pudo actualizar secondary image', err);
    }
  }

  // Subscribe to the two events that variant-picker dispatches
  document.addEventListener('on:variant:init', handleVariantEvent);
  document.addEventListener('on:variant:change', handleVariantEvent);

  document.querySelectorAll('variant-picker').forEach((vp) => {
    try {
      const instVariant = vp.variant || (vp.__variant && vp.__variant) || null;
      if (instVariant) {
        handleVariantEvent({ target: vp, detail: { variant: instVariant } });
      }
    } catch (e) { }
  });


})();
